import React, { useState } from "react";
import { MessageCircle, Mail, MessageSquareText } from "lucide-react";
import { cn } from "@/lib/cn";
import { Tooltip } from "@/components/atoms/Tooltip";
import { CommunicationChannelList } from "./CommunicationChannelList";
import { EmailChannelList } from "./EmailChannelList";

type Channel = "whatsapp" | "email" | "sms";

const channels: { id: Channel; label: string; icon: React.ElementType; disabled?: boolean }[] = [
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { id: "email", label: "Email", icon: Mail },
  { id: "sms", label: "SMS", icon: MessageSquareText, disabled: true },
];

/**
 * Sub-tab switcher inside the "Communication Channels" integrations tab.
 * Each pill renders an independent channel configuration screen. WhatsApp is
 * the existing implementation and is left untouched; Email is a new UI-only
 * screen; SMS is a placeholder until the channel ships.
 */
export const CommunicationChannelTabs: React.FC = () => {
  const [active, setActive] = useState<Channel>("whatsapp");

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        {channels.map((ch) => {
          const isActive = active === ch.id;
          const button = (
            <button
              key={ch.id}
              type="button"
              onClick={() => !ch.disabled && setActive(ch.id)}
              aria-disabled={ch.disabled}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all",
                ch.disabled
                  ? "border-bridge-border bg-white text-bridge-muted/50 cursor-not-allowed"
                  : isActive
                    ? "border-bridge-accent bg-bridge-accent/10 text-bridge-accent"
                    : "border-bridge-border bg-white text-bridge-muted hover:text-bridge-fg hover:border-bridge-border",
              )}
            >
              <ch.icon className="w-3.5 h-3.5" />
              {ch.label}
            </button>
          );

          return ch.disabled ? (
            <Tooltip key={ch.id} content="Coming Soon" side="top">
              {button}
            </Tooltip>
          ) : (
            button
          );
        })}
      </div>

      {active === "whatsapp" && <CommunicationChannelList />}
      {active === "email" && <EmailChannelList />}
      {active === "sms" && (
        <div className="flex flex-col items-center justify-center text-center py-16">
          <div className="p-3 bg-bridge-bg rounded-full mb-3">
            <MessageSquareText className="w-10 h-10 text-bridge-muted/30" />
          </div>
          <h3 className="text-base font-semibold text-bridge-fg">SMS Coming Soon</h3>
          <p className="text-xs text-bridge-muted max-w-sm mt-2">
            SMS channel configuration will be available here once the integration is ready.
          </p>
        </div>
      )}
    </div>
  );
};
